import { type ElementType, HTMLAttributes } from 'react';
import './Heading.css';

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type HeadingAlign = 'left' | 'center' | 'right';
/**
 * Named visual styles, decoupled from the semantic level.
 * display ≙ level 1, section ≙ level 2, subsection ≙ level 3,
 * card ≙ level 4, label ≙ level 6 (small, secondary).
 */
export type HeadingVariant = 'display' | 'section' | 'subsection' | 'card' | 'label';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span' | 'p';

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Semantic heading level — determines default rendered element and token-based font size. Defaults to 2. */
  level?: HeadingLevel;
  /** Named visual style that overrides the level-based size/weight/colour */
  variant?: HeadingVariant;
  /** Override the rendered HTML element while keeping level-based styling */
  as?: HeadingTag;
  /** Text alignment */
  align?: HeadingAlign;
  /** Additional CSS class names */
  className?: string;
}

const LEVEL_TAGS: Record<HeadingLevel, HeadingTag> = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
};

/**
 * Heading atom — the single heading implementation of the design system.
 *
 * The `level` prop determines both the default rendered element and the token-based
 * font size class (`heading--{level}`). Use `as` to decouple the visual level from the
 * semantic element, and `variant` to apply a named visual style
 * (`heading--variant-{variant}`) on top of the level styling.
 *
 * All sizing and colour values come exclusively from design-system CSS custom properties.
 */
export function Heading({
  level = 2,
  variant,
  as,
  align,
  className = '',
  children,
  ...rest
}: HeadingProps) {
  // eslint-disable-next-line security/detect-object-injection
  const Tag = as ?? LEVEL_TAGS[level];
  const combinedClass = [
    'heading',
    `heading--${level}`,
    variant ? `heading--variant-${variant}` : '',
    align ? `heading--align-${align}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const Element = Tag as unknown as ElementType;

  return (
    <Element className={combinedClass} {...rest}>
      {children}
    </Element>
  );
}
